import { ConnectionState, EStreamTradeDTO, SSESource, SubscribeOptions, UserStreamMessage } from '../../types';
import { AccountBalance, CurrencyExchangeRates, InventoryDetailsMaybe } from '../portfolio';

type EventCallback<T extends DispatchEvent> = (event?: T) => void;

class DispatchEvent {
  constructor(
    public name: string,
    public options?: any,
  ) {}
}

class PubSub {
  private events: Record<string, EventCallback<DispatchEvent>[]> = {};

  addEventListener(event: string, callback: EventCallback<DispatchEvent>): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }

    this.events[event].push(callback);
  }

  dispatchEvent(event: DispatchEvent): void {
    const eventCallbacks = this.events[event.name];

    if (eventCallbacks && eventCallbacks.length > 0) {
      eventCallbacks.forEach(callback => {
        callback(event);
      });
    }
  }

  removeEventListener(event: string, callback: EventCallback<any>): void {
    const eventCallbacks = this.events[event];

    if (eventCallbacks && eventCallbacks.length > 0) {
      this.events[event] = eventCallbacks.filter(cb => cb !== callback);
    }
  }
}

export class AppEventDispatcher extends PubSub {}

export const appEventDispatcher = new AppEventDispatcher();

export const onAppEvent = <T extends DispatchEvent>(key: string, callback: (event: T) => void | Promise<void>) => {
  appEventDispatcher.addEventListener(key, callback as EventCallback<DispatchEvent>);
  return () => {
    appEventDispatcher.removeEventListener(key, callback);
  };
};

export namespace AppEvents {
  export type MultiBrowserNetBrokerConfig = {
    jwtToken: string;
    idToken: string;
    eStreamSessionId: string;
    userStreamConnectionState: ConnectionState;
    eStreamConnectionState: ConnectionState;
    inventoryDetails: InventoryDetailsMaybe;
    accountBalance: Array<AccountBalance>;
    currencyExchangeRates: CurrencyExchangeRates;
  };

  export type SSEConnectionStateUpdate = {
    source: SSESource;
    connectionState: ConnectionState;
    isInitalValue: boolean;
  };

  export type EStreamTradesReceived = {
    trades: Array<EStreamTradeDTO> | EStreamTradeDTO;
  };

  export type EStreamSubscriptionChange = {
    dir: 'subscribe' | 'unsubscribe';
    options: Array<SubscribeOptions>;
    instanceId: number;
  };

  export class ThisInstanceSelectedAsPrimaryEvent extends DispatchEvent {
    static type = 'ThisInstanceSelectedAsPrimary';

    constructor(public data: Array<SubscribeOptions>) {
      super(ThisInstanceSelectedAsPrimaryEvent.type);
    }
  }

  export class SSEConnectionStateUpdateEvent extends DispatchEvent {
    static type = 'SSEConnectionStateUpdate';

    constructor(public data: SSEConnectionStateUpdate) {
      super(SSEConnectionStateUpdateEvent.type, { bubbles: true, cancelable: true });
    }
  }

  export class EStreamTradesReceivedEvent extends DispatchEvent {
    static type = 'EStreamTradesReceived';

    constructor(public data: EStreamTradesReceived) {
      super(EStreamTradesReceivedEvent.type);
    }
  }

  export class OtherInstanceLoginStartedEvent extends DispatchEvent {
    static type = 'OtherInstanceLoginStarted';

    constructor() {
      super(OtherInstanceLoginStartedEvent.type);
    }
  }

  export class OtherInstanceLoggedInEvent extends DispatchEvent {
    static type = 'OtherInstanceLoggedIn';

    constructor(public data: MultiBrowserNetBrokerConfig) {
      super(OtherInstanceLoggedInEvent.type);
    }
  }

  export class OtherInstanceLoggedOutEvent extends DispatchEvent {
    static type = 'OtherInstanceLoggedOut';

    constructor() {
      super(OtherInstanceLoggedOutEvent.type);
    }
  }

  export class OtherInstanceDestroyedEvent extends DispatchEvent {
    static type = 'OtherInstanceDestroyed';

    constructor(public instanceId: number) {
      super(OtherInstanceDestroyedEvent.type);
    }
  }

  export class EStreamSubscriptionChangeEvent extends DispatchEvent {
    static type = 'EStreamSubscriptionChange';

    constructor(public data: EStreamSubscriptionChange) {
      super(EStreamSubscriptionChangeEvent.type);
    }
  }

  export class UserStreamMessageEvent extends DispatchEvent {
    static type = 'UserStreamMessage';

    constructor(
      public data: UserStreamMessage,
      public isReceivedFromAnotherInstance: boolean,
    ) {
      super(UserStreamMessageEvent.type);
    }
  }

  export class UpdateUserActivityEvent extends DispatchEvent {
    static type = 'UpdateUserActivityEvent';

    constructor() {
      super(UpdateUserActivityEvent.type);
    }
  }
}
